/**
 * Defensive sanitizer for story text coming from AI.
 * If the AI accidentally leaked raw JSON (story_text / choices / encounter)
 * into the narrative field, we extract only the `story_text` value and
 * strip the rest. Otherwise returns the text as-is.
 */
export function sanitizeStoryText(text, lang = "en") {
  if (!text || typeof text !== "string") return text || "";
  let trimmed = text.trim();

  // Strip markdown code fences (```json ... ``` or ``` ... ```) that Gemini occasionally leaks
  const fenceMatch = trimmed.match(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/);
  if (fenceMatch) {
    trimmed = fenceMatch[1].trim();
  }

  // Quick heuristic: only attempt if it looks like JSON
  if (!trimmed.startsWith("{") || !trimmed.includes('"story_text"')) {
    return trimmed === text.trim() ? text : trimmed;
  }

  // Try to parse and extract story_text
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed.story_text === "string" && parsed.story_text.trim()) {
      return parsed.story_text;
    }
  } catch {
    // Try regex fallback on story_text value (handle escaped quotes)
    const m = trimmed.match(/"story_text"\s*:\s*"((?:\\.|[^"\\])*)"/s);
    if (m && m[1]) {
      // unescape common sequences
      return m[1]
        .replace(/\\n/g, "\n")
        .replace(/\\r/g, "\r")
        .replace(/\\t/g, "\t")
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, "\\");
    }
  }

  // Last resort: generic placeholder to avoid showing raw JSON to the player
  return lang.startsWith("it")
    ? "La pergamena si rovina: l'inchiostro si è sbiadito e le parole non sono leggibili. (Generazione non riuscita, riprova la scelta.)"
    : "The scroll is damaged: the ink has faded and the words are unreadable. (Generation failed — please retry your choice.)";
}
